import { zodResolver } from "@hookform/resolvers/zod";
import { Turnstile } from "@marsidev/react-turnstile";
import { Link, useSearch } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { CookieSettingsButton } from "@/components/CookieSettingsButton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { authClient } from "../../lib/auth";
import LoadingSpinner from "../loading-spinner";
import { Field, FieldError, FieldGroup, FieldLabel } from "../ui/field";
import GoogleIcon from "./GoogleIcon";
import { googleErrorMessage } from "./googleErrorMessage";

// Zod validation schema
const loginSchema = z.object({
    email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
    password: z.string().min(1, "Password is required").min(8, "Password must be at least 8 characters long"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm({ className, invitation, ...props }: React.ComponentProps<"div"> & { invitation: any }) {
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [token, setToken] = useState("");
    const search = useSearch({ strict: false }) as {
        redirect: string | undefined;
        email: string | undefined;
        error: string | undefined;
        client_id?: string;
        response_type?: string;
    };
    const { redirect, email, error } = search;

    // When the OIDC provider redirects an unauthenticated OAuth/MCP client
    // here, it forwards the original query string (client_id, redirect_uri,
    // response_type, ...). After login we send the user back to the authorize
    // endpoint so the flow can continue.
    const postLoginRedirect = (() => {
        if (search.client_id && search.response_type) {
            const authBase = import.meta.env.VITE_AUTH_BASE_URL || window.location.origin;
            const params = new URLSearchParams();
            for (const [key, value] of Object.entries(search)) {
                if (value !== undefined) params.set(key, String(value));
            }
            return `${authBase}/api/auth/oauth2/authorize?${params.toString()}`;
        }
        return redirect;
    })();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        mode: "onBlur",
        defaultValues: {
            email: email,
        },
    });

    const onSubmit = async (data: LoginFormData) => {
        setIsLoading(true);
        setLoginError(null);
        const { error, data: authData } = await authClient.signIn.email({
            email: data.email,
            password: data.password,
            //callbackURL: redirect || window.location.origin,
            rememberMe: true,
            fetchOptions: {
                headers: {
                    "x-captcha-response": token,
                },
            },
        });

        if (error) {
            console.error("Login error:", error);
            setLoginError(error.message || "Login failed. Please try again.");
            setIsLoading(false);
            if (window.turnstile) {
                window.turnstile.reset(); // Resets all widgets on the page
            }
        } else {
            // the callbackurl doesn't work in signup so we 're doing it the same here.
            window.location.href = postLoginRedirect || window.location.origin;
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoadingGoogle(true);
        try {
            const redirectUrl = postLoginRedirect || undefined;

            await authClient.signIn.social({
                provider: "google",
                callbackURL: redirectUrl || window.location.origin,
                errorCallbackURL: `${window.location.origin}/login`,
            });
        } catch (error) {
            setIsLoadingGoogle(false);
            console.error("Google login error:", error);
            throw error;
        }
    };

    const id = useId();

    return (
        <div className={cn("flex flex-col gap-6", className)} {...props}>
            <Card>
                <CardHeader className="text-center">
                    <CardTitle className="text-xl">
                        <img src="/orsane.svg" alt="Orsane" className="w-auto h-4 mx-auto mb-2" />
                    </CardTitle>
                    <CardDescription className="text-neutral-600">
                        {invitation
                            ? `You've been invited to the '${invitation.organization.name}' team. Login or sign up to accept the invitation.`
                            : "Login with your Google account or email address"}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} id={id}>
                        <div className="grid gap-6">
                            <div className="flex flex-col gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full text-base"
                                    onClick={handleGoogleLogin}
                                    disabled={isLoadingGoogle || isLoading}
                                >
                                    {isLoadingGoogle && <LoadingSpinner />}
                                    {!isLoadingGoogle && <GoogleIcon />}
                                    Login with Google
                                </Button>
                                <FieldError className="-mt-1">{googleErrorMessage(error)}</FieldError>
                            </div>
                            <div className="after:border-border relative text-center  after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                                <span className="bg-card text-muted-foreground relative z-10 px-2">
                                    Or continue with
                                </span>
                            </div>

                            {loginError && (
                                <div className=" text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                                    {loginError}
                                </div>
                            )}

                            <div className="grid gap-4">
                                <FieldGroup className="gap-4">
                                    <Field>
                                        <FieldLabel htmlFor="email">Email</FieldLabel>
                                        <Input
                                            type="email"
                                            placeholder="m@example.com"
                                            {...register("email")}
                                            className={errors.email ? "border-red-500" : ""}
                                        />
                                        <FieldError errors={[errors.email]} />
                                    </Field>
                                    <Field>
                                        <div className="flex items-center">
                                            <FieldLabel htmlFor="password">Password</FieldLabel>

                                            <a
                                                href="/forgot-password"
                                                className="ml-auto  underline-offset-4 hover:underline"
                                                tabIndex={-1}
                                            >
                                                Forgot your password?
                                            </a>
                                        </div>
                                        <Input
                                            type="password"
                                            {...register("password")}
                                            className={errors.password ? "border-red-500" : ""}
                                        />
                                        <FieldError errors={[errors.password]} />
                                    </Field>
                                </FieldGroup>

                                <Turnstile
                                    onSuccess={setToken}
                                    tabIndex={-1}
                                    className="w-full mx-auto"
                                    options={{
                                        theme: "light",
                                        size: "flexible",
                                    }}
                                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY!}
                                />
                                <Button
                                    type="submit"
                                    form={id}
                                    className="w-full [&_iframe]:mx-auto text-base"
                                    disabled={isLoading || !token}
                                >
                                    {isLoading ? "Logging in..." : "Login"}
                                </Button>
                            </div>
                            <div className="text-center text-neutral-600">
                                Don&apos;t have an account?{" "}
                                <Link
                                    to="/signup"
                                    className="underline underline-offset-4"
                                    search={{
                                        redirect: redirect,
                                        email: email,
                                    }}
                                >
                                    Sign up
                                </Link>
                            </div>
                        </div>
                    </form>
                </CardContent>
            </Card>
            <div className="text-muted-foreground text-center text-sm">
                <CookieSettingsButton>Cookie settings</CookieSettingsButton>
            </div>
        </div>
    );
}
